import { Fragment, useMemo } from 'react';
import { Button, Card, FloatingButton, Header, IconClose, LineBreak, useVStack } from '@direct-frontend/components';
import { useMobileView } from '#shared/lib/platform';
import { Modal } from '#shared/ui/Modal';
import { type ModalCommunication } from '../../model/selectModalCommunication';
import { communicationModalTestIds } from '../../testids';
import styles from './CommunicationModalView.module.css';

type CommunicationModalViewProps = {
    communication: ModalCommunication;
    isOpen: boolean;
    onClose: () => void;
    onAction: () => void;
    onCloseComplete: () => void;
};

export const CommunicationModalView = ({
    communication,
    isOpen,
    onClose,
    onAction,
    onCloseComplete,
}: CommunicationModalViewProps) => {
    const { image, title, description } = communication.details.modal;
    const { button } = communication;
    const contentClassName = useVStack({ gap: '28' });
    const isMobileView = useMobileView();
    const caption = useMemo(
        () =>
            description
                ? description.split(/\n+/).map((line, index) => (
                      <Fragment key={index}>
                          {index > 0 && <LineBreak />}
                          {line}
                      </Fragment>
                  ))
                : undefined,
        [description]
    );

    return (
        <Modal
            className={isMobileView ? styles.mobileContent : undefined}
            size="small"
            withoutPaddings
            isOpen={isOpen}
            onClose={onClose}
            onAnimationEnd={(state) => {
                if (state === 'close') {
                    onCloseComplete();
                }
            }}
            dismissOnEsc={false}
            dismissOnOutsideClick={false}
            data-testid={communicationModalTestIds.root}
        >
            {isMobileView && (
                <FloatingButton
                    className={styles.closeButton}
                    icon={IconClose}
                    size="2xs"
                    color="contrast"
                    aria-label="Закрыть"
                    onClick={onClose}
                    data-testid={communicationModalTestIds.close}
                />
            )}
            {/* eslint-disable-next-line @direct-frontend/components/no-restricted-native-elements -- картинка из конфига, фиксированный размер не задан */}
            <img src={image.file_path} alt="" className={styles.image} />
            <Card padding="48" paddingCorrection="top" className={contentClassName}>
                <Header level="h2" textAlign="center" caption={caption} className={styles.header}>
                    {title}
                </Header>
                {button && (
                    <Button
                        {...(button.url
                            ? { as: 'a', href: button.url, target: '_blank', rel: 'noopener noreferrer' }
                            : {})}
                        color="accent"
                        size="l"
                        width="max"
                        onClick={onAction}
                        data-testid={communicationModalTestIds.action}
                    >
                        {button.text}
                    </Button>
                )}
            </Card>
        </Modal>
    );
};
